import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import ProjectCard from './ProjectCard';
import config from '../config';
import {getProjectThumbnail} from '../utils/screenshotCapture';
import {getBffHeaders} from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './Home.css';

// Utility functions for managing recent projects via API
const MAX_RECENT_PROJECTS = 3;

const getRecentProjects = async (userEmail = 'demo-user', tenantId = null) => {
  try {
    const baseUrl = config.bffUrl || window.location.origin;
    let url = `${baseUrl}/api/recent-projects`;
    
    // Add tenantId query parameter if provided
    if (tenantId) {
      url += `?tenantId=${encodeURIComponent(tenantId)}`;
    }
    
    const response = await getAuthorizedFetch()(url, {
      method: 'GET',
      credentials: 'include',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userEmail,
      }),
    });
    
    if (!response.ok) {
      console.warn('Failed to fetch recent projects from API');
      return [];
    }
    
    const data = await response.json();
    return data.recentProjects || [];
  } catch (error) {
    console.error('Failed to load recent projects:', error);
    return [];
  }
};

const addRecentProject = async (project, userEmail = 'demo-user') => {
  try {
    const baseUrl = config.bffUrl || window.location.origin;
    const response = await getAuthorizedFetch()(`${baseUrl}/api/recent-projects`, {
      method: 'POST',
      credentials: 'include',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userEmail,
      }),
      body: JSON.stringify(project),
    });
    
    if (!response.ok) {
      console.warn('Failed to save recent project to API');
    }
  } catch (error) {
    console.error('Failed to save recent project:', error);
  }
};

/**
 * Common utility to activate code layout (canvas mode) with optional project context
 * @param {Object} options - Configuration options
 * @param {string} options.tenantId - Tenant ID (optional)
 * @param {string} options.projectId - Project ID (optional)
 * @param {Object} options.tenant - Full tenant object (optional)
 * @param {Object} options.project - Full project object (optional)
 * @param {Object} options.revision - Revision object (optional)
 */
const activateCodeLayout = ({tenantId, projectId, tenant, project, revision} = {}) => {
  try {
    // Always activate canvas mode
    localStorage.setItem('canvasMode', 'true');
    
    // If project context is provided, store it
    if ((tenant || tenantId) && (project || projectId)) {
      const projectContext = {
        tenant: tenant || {tenantId},
        project: project || {
          _id: projectId,
          projectId: projectId,
        },
        revision: revision || null
      };
      
      localStorage.setItem('canvasProjectContext', JSON.stringify(projectContext));
    }
    
    console.log('✅ Code layout activated', {tenantId, projectId});
  } catch (error) {
    console.error('Failed to activate code layout:', error);
  }
};

const Home = ({
  onStartChat,
  onSelectProject,
  onSelectTemplate,
  onNavigate,
  projects = [],
  templates = [],
  user,
  selectedTenant,
  hasGitConnection = false,
  playbooks = [],
  onLaunchIntent,
}) => {
  const [view, setView] = useState('welcome'); // welcome, projects, templates
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [recentProjects, setRecentProjects] = useState([]);
  const [starredProjects, setStarredProjects] = useState([]);
  const [recentProjectsVisited, setRecentProjectsVisited] = useState([]);
  const [showAdvancedCTA, setShowAdvancedCTA] = useState(() => {
    const saved = localStorage.getItem('showAdvancedCTA');
    return saved === null ? false : saved === 'true';
  });
  const [showPopularTemplates, setShowPopularTemplates] = useState(() => {
    const saved = localStorage.getItem('showPopularTemplates');
    return saved === null ? false : saved === 'true';
  });
  const [textareaExpanded, setTextareaExpanded] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = React.useRef(0);
  const fileInputRef = React.useRef(null);
  
  // Mode selection state for omnibar
  const [selectedMode, setSelectedMode] = useState('chat');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [shakeMode, setShakeMode] = useState(null);
  const [showComingSoonToast, setShowComingSoonToast] = useState(false);
  
  // FIXME: Replace with BFF API call to fetch templates
  const fullStackTemplates = [
    'React + Node.js Application',
    'React + .NET Core Application',
    'React + Java Application',
    'Only Backend Java',
    'Only Backend .NET',
    'Only Backend Node.js',
    'Next.js Application',
  ];
  
  // Start with 2 hexagons and spawn more over time
  const [hexagons, setHexagons] = useState(() => {
    return Array.from({length: 2}, (_, i) => ({
      id: i,
      size: Math.floor(Math.random() * 251) + 50, // Random between 50-300px
      isNew: false,
    }));
  });

  // Spawn hexagons over time (every 5-10 seconds until reaching 8)
  useEffect(() => {
    if (hexagons.length >= 8) return;
    
    const spawnInterval = Math.random() * 5000 + 5000; // Random 5-10 seconds
    const timer = setTimeout(() => {
      setHexagons(prev => {
        if (prev.length >= 8) return prev;
        return [...prev, {
          id: prev.length,
          size: Math.floor(Math.random() * 251) + 50,
          isNew: true,
        }];
      });
    }, spawnInterval);
    
    // Cleanup timer on unmount to prevent memory leaks
    return () => clearTimeout(timer);
  }, [hexagons.length]);

  // Listen for feature toggle changes
  useEffect(() => {
    const handleFeatureToggle = (event) => {
      if (event.detail && 'showAdvancedCTA' in event.detail) {
        setShowAdvancedCTA(event.detail.showAdvancedCTA);
      }
      if (event.detail && 'showPopularTemplates' in event.detail) {
        setShowPopularTemplates(event.detail.showPopularTemplates);
      }
    };
    window.addEventListener('featureToggleChanged', handleFeatureToggle);
    return () => window.removeEventListener('featureToggleChanged', handleFeatureToggle);
  }, []);

  // Load recent projects from API on mount and when tenant changes
  useEffect(() => {
    const userEmail = user?.email || user?.sub || 'demo-user';
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id;
    
    const loadRecentProjects = async () => {
      const recent = await getRecentProjects(userEmail, tenantId);
      // If no recent projects from API, use the first 3 from projects prop
      if (recent.length === 0 && projects.length > 0) {
        const sortedProjects = [...projects].sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt || 0);
          const dateB = new Date(b.updatedAt || b.createdAt || 0);
          return dateB - dateA;
        });
        setRecentProjectsVisited(sortedProjects.slice(0, MAX_RECENT_PROJECTS));
      } else {
        setRecentProjectsVisited(recent);
      }
      
      // Fetch thumbnails for recent projects in the background
      const projectsToFetch = recent.length > 0 ? recent : (projects.length > 0 ? projects.slice(0, MAX_RECENT_PROJECTS) : []);
      projectsToFetch.forEach(async (project) => {
        try {
          // Extract tenantId from project or user context
          const projectTenantId = project.tenant?.tenantId || project.tenantId;
          const thumbnail = await getProjectThumbnail(project._id || project.projectId, projectTenantId);
          if (thumbnail) {
            setRecentProjectsVisited(prevProjects => 
              prevProjects.map(p => 
                (p._id === project._id || p.projectId === project._id) ? { ...p, thumbnail } : p
              )
            );
          }
        } catch (error) {
          // Silently ignore thumbnail fetch errors
          console.debug(`Failed to fetch thumbnail for project ${project._id}:`, error);
        }
      });
    };
    
    loadRecentProjects();
  }, [projects, user, selectedTenant]);

  useEffect(() => {
    // Load recent and starred projects from props
    const sortedProjects = [...projects].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    });
    setRecentProjects(sortedProjects.slice(0, 6));
    
    const starred = projects.filter(p => p.starred);
    setStarredProjects(starred);
    
    // Fetch thumbnails for all projects in the background
    const allProjects = [...sortedProjects.slice(0, 6), ...starred];
    allProjects.forEach(async (project) => {
      try {
        const tenantId = project.tenant?.tenantId || project.tenantId;
        const thumbnail = await getProjectThumbnail(project._id || project.projectId, tenantId);
        if (thumbnail) {
          // Update both recentProjects and starredProjects
          setRecentProjects(prevProjects => 
            prevProjects.map(p => 
              (p._id === project._id || p.projectId === project._id) ? { ...p, thumbnail } : p
            )
          );
          setStarredProjects(prevProjects => 
            prevProjects.map(p => 
              (p._id === project._id || p.projectId === project._id) ? { ...p, thumbnail } : p
            )
          );
        }
      } catch (error) {
        // Silently ignore thumbnail fetch errors
        console.debug(`Failed to fetch thumbnail for project ${project._id}:`, error);
      }
    });
  }, [projects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           (selectedCategory === 'starred' && project.starred) ||
                           (selectedCategory === 'recent' && recentProjects.includes(project));
    return matchesSearch && matchesCategory;
  });

  const filteredTemplates = templates.filter(template => {
    return template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           template.description?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get dynamic placeholder text based on selected mode
  const getPlaceholderText = () => {
    // Check if selected mode is a playbook
    const activePlaybook = playbooks.find(i => `playbook:${i.itemId}` === selectedMode);
    if (activePlaybook) {
      return activePlaybook.homePrompt
        ? activePlaybook.homePrompt
        : `Ask ${activePlaybook.name}...`;
    }
    switch (selectedMode) {
      case 'mytasks':
        return 'See what to do next...';
      case 'chat':
        return 'Describe your idea...';
      case 'webapp':
        return 'Ask Mia Flow to create a web app that...';
      case 'fullstack':
        return selectedTemplate 
          ? `Describe your ${selectedTemplate}...`
          : 'Select a template and describe your full stack application...';
      default:
        return 'Ask Mia Flow to create a web app that...';
    }
  };

  // Handle mode selection with coming soon feedback
  const handleModeSelect = (mode) => {
    // Playbook mode
    if (mode.startsWith('playbook:')) {
      const playbookId = mode.slice('playbook:'.length);
      const playbook = playbooks.find(i => i.itemId === playbookId);
      // 'code'-only playbooks require a git connection; 'both' allows chat fallback so skip the guard
      if (playbook?.mode === 'code' && !hasGitConnection) {
        setShakeMode(mode);
        setShowComingSoonToast(true);
        setTimeout(() => setShakeMode(null), 500);
        setTimeout(() => setShowComingSoonToast(false), 2500);
        onNavigate && onNavigate('/tools');
        return;
      }
      setSelectedMode(mode);
      setShakeMode(null);
      setShowTemplateDropdown(false);
      return;
    }
    if ((mode === 'webapp' || mode === 'fullstack') && !hasGitConnection) {
      // No git provider connected — redirect to tools
      setShakeMode(mode);
      setShowComingSoonToast(true);
      setTimeout(() => setShakeMode(null), 500);
      setTimeout(() => setShowComingSoonToast(false), 2500);
      onNavigate && onNavigate('/tools');
      return;
    }
    if (mode === 'webapp' || mode === 'fullstack' || mode === 'chat') {
      setSelectedMode(mode);
      setShakeMode(null);
      if (mode === 'fullstack') {
        // Always show template dropdown when Full Stack is clicked
        setShowTemplateDropdown(true);
      } else {
        setShowTemplateDropdown(false);
      }
    } else {
      // Coming soon modes - show shake animation and toast
      setShakeMode(mode);
      setShowComingSoonToast(true);
      setTimeout(() => setShakeMode(null), 500);
      setTimeout(() => setShowComingSoonToast(false), 2500);
    }
  };

  // Handle omnibar submit
  const handleOmnibarSubmit = (value) => {
    if (!value.trim()) return;
    
    // Playbook mode
    if (selectedMode.startsWith('playbook:')) {
      const playbookId = selectedMode.slice('playbook:'.length);
      const playbook = playbooks.find(i => i.itemId === playbookId);
      if (playbook && onLaunchIntent) {
        onLaunchIntent(playbook, value);
      }
      setAttachedFiles([]);
      return;
    }
    
    if (selectedMode === 'webapp') {
      // Web App mode - current default behavior
      activateCodeLayout();
      handleStartChat(value, 'code', attachedFiles);
    } else if (selectedMode === 'chat') {
      // Chat mode - go straight to Chat (/compose), no project creation
      handleStartChat(value, 'chat', attachedFiles);
    } else if (selectedMode === 'fullstack' && selectedTemplate) {
      // Full Stack mode - activate with template context
      activateCodeLayout();
      handleStartChat(`Create a ${selectedTemplate}: ${value}`, 'code', attachedFiles, selectedTemplate);
    } else if (selectedMode === 'fullstack' && !selectedTemplate) {
      // Full stack without template - show dropdown
      setShowTemplateDropdown(true);
      return;
    }
    // Other modes do nothing (coming soon)
    setAttachedFiles([]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...selectedFiles]);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleStartChat = (prompt = '', mode = 'code', files = [], fullStackTemplate = null) => {
    if (onStartChat) {
      onStartChat(prompt, mode, files, fullStackTemplate);
    }
  };

  const renderWelcomeView = () => (
    <div className="home-welcome">
      {/* Animated hexagons - covering entire welcome section */}
      <div className="home-hexagon-container">
        {hexagons.map((hex, i) => (
          <div 
            key={hex.id} 
            className={`home-hexagon home-hexagon-${(i % 8) + 1}${hex.isNew ? ' hexagon-spawn' : ''}`}
            style={{
              width: `${hex.size}px`,
              height: `${hex.size}px`,
            }}
          >
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon points="50 0, 93.301 25, 93.301 75, 50 100, 6.699 75, 6.699 25" />
            </svg>
          </div>
        ))}
      </div>
      
      {/* Template Dropdown Modal - Positioned at root level */}
      {selectedMode === 'fullstack' && showTemplateDropdown && (
        <div className="template-dropdown-overlay" onClick={() => setShowTemplateDropdown(false)}>
          <div className="template-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="template-dropdown-header">
              <h3>Select a Template</h3>
              <button 
                className="template-dropdown-close"
                onClick={() => setShowTemplateDropdown(false)}
              >
                <Icon name="x" size={20} />
              </button>
            </div>
            <div className="template-dropdown-list">
              {fullStackTemplates.map((template) => (
                <button
                  key={template}
                  className={`template-dropdown-item ${selectedTemplate === template ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowTemplateDropdown(false);
                  }}
                >
                  <Icon name="full-stack" size={18} />
                  <span>{template}</span>
                  {selectedTemplate === template && <Icon name="check" size={16} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Hero Section with Gradient */}
      <div className={`home-hero ${!showAdvancedCTA ? 'compact' : ''}`}>
        <div className="home-hero-content">
          <h1 className="home-title">Ready to flow, {user?.name?.split(' ')[0] || 'Giulio'}!</h1>
          <p className="home-subtitle">Turn thoughts into code. Build from scratch or modernize legacy apps instantly.</p>
          
          {/* Coming Soon Toast - Fixed Overlay */}
          {showComingSoonToast && (
            <div className="coming-soon-toast">
              <Icon name="info" size={18} />
              <span>Coming Soon</span>
            </div>
          )}
          
          {/* Large Omnibar Input */}
          <div className={`home-omnibar-container ${!showAdvancedCTA ? 'compact' : ''}`}>
            <div
              className={`home-omnibar${isDraggingOver ? ' drag-over' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/markdown,text/csv,text/html,text/xml,text/css,text/javascript,application/json,application/xml,.md,.yaml,.yml,.toml,.ini,.env,.jsx,.ts,.tsx,.mjs,.py,.rb,.php,.java,.kt,.swift,.go,.rs,.c,.cpp,.h,.cs,.sh,.sql,.graphql,.proto,.tf,.log,.diff,.docx,.xlsx,.pptx"
                style={{display: 'none'}}
                onChange={handleFileInputChange}
              />
              {isDraggingOver && (
                <div className="omnibar-drag-overlay">
                  <Icon name="upload" size={32} />
                  <span>Drop files to attach</span>
                </div>
              )}
              <textarea
                rows={textareaExpanded ? 10 : 3}
                className="omnibar-input"
                placeholder={getPlaceholderText()}
                onKeyDown={(e) => {
                  // CMD+Enter (Mac) or Ctrl+Enter (Windows) to submit
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && e.target.value.trim()) {
                    e.preventDefault();
                    handleOmnibarSubmit(e.target.value);
                  }
                  // Regular Enter creates a new line (no preventDefault)
                }}
                onInput={(e) => {
                  // Auto-grow textarea up to max-height, then allow internal scrolling
                  const maxHeight = 400;
                  e.target.style.height = 'auto';
                  const newHeight = Math.min(e.target.scrollHeight, maxHeight);
                  e.target.style.height = newHeight + 'px';
                  e.target.style.overflowY = e.target.scrollHeight > maxHeight ? 'auto' : 'hidden';
                }}
              />
              {attachedFiles.length > 0 && (
                <div className="omnibar-attached-files">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="omnibar-file-chip">
                      <Icon name="file" size={14} />
                      <span className="file-chip-name" title={file.name}>{file.name}</span>
                      <button
                        className="file-chip-remove"
                        onClick={() => handleRemoveFile(index)}
                        title="Remove file"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="omnibar-actions-bottom">
                
                
                {/* Mode Selection Buttons - Center */}
                <div className="omnibar-mode-actions">
                  <button 
                    className={`mode-btn ${selectedMode === 'chat' ? 'active' : ''}`}
                    onClick={() => handleModeSelect('chat')}
                    title="Chat"
                  >
                    <Icon name="brainstorming" size={18} />
                    <span>Chat</span>
                  </button>
                  
                  <button 
                    className={`mode-btn ${selectedMode === 'webapp' ? 'active' : ''}`}
                    onClick={() => handleModeSelect('webapp')}
                    title="Web App"
                  >
                    <Icon name="code" size={18} />
                    <span>Web App</span>
                  </button>
                  

                  {playbooks.map(playbook => {
                    const hasTemplates = !!(playbook.homeProjectTemplateId || playbook.homeSourceTemplateId);
                    const templateTip = hasTemplates
                      ? `\nProject template: ${playbook.homeProjectTemplateId || '—'}\nSource template: ${playbook.homeSourceTemplateId || '—'}`
                      : '';
                    const modeClass = playbook.mode === 'code'
                      ? ' mode-btn-playbook-code'
                      : playbook.mode === 'both'
                        ? ' mode-btn-playbook-both'
                        : '';
                    const modeIcon = playbook.mode === 'code' ? 'code' : 'zap';
                    return (
                      <button
                        key={playbook.itemId}
                        className={`mode-btn mode-btn-playbook${modeClass} ${selectedMode === `playbook:${playbook.itemId}` ? 'active' : ''} ${shakeMode === `playbook:${playbook.itemId}` ? 'shake' : ''}`}
                        onClick={() => handleModeSelect(`playbook:${playbook.itemId}`)}
                        title={`${playbook.description || playbook.name}${templateTip}`}
                      >
                        <Icon name={modeIcon} size={18} />
                        <span>{playbook.name}</span>
                        {playbook.mode === 'both' && <span className="mode-btn-both-dot" title="Chat or Code" />}
                        {hasTemplates && <span className="mode-btn-template-dot" title="Uses project template" />}
                      </button>
                    );
                  })}
                </div>
                
                <div className="omnibar-right-actions">
                  <button
                    className="omnibar-attach-btn"
                    title="Attach files"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                    </svg>
                  </button>
                  <button 
                    className="omnibar-send-btn"
                    title={`Send (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter)`}
                    onClick={() => {
                      const input = document.querySelector('.omnibar-input');
                      if (input?.value.trim()) {
                        handleOmnibarSubmit(input.value);
                      }
                    }}
                  >
                    <Icon name="arrow-right" size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* AI Assistant Shortcuts - Compact - REMOVED, now integrated in omnibar */}
          </div>

          {/* Quick Start Cards */}
          {showAdvancedCTA && (
            <div className="home-quick-start">
              <button 
                className="quick-start-card"
                onClick={() => onNavigate && onNavigate('/projects')}
              >
                <div className="card-icon">
                  <Icon name="folder" size={24} />
                </div>
                <div className="card-content">
                  <h3>Browse Projects</h3>
                  <p>View all your projects</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* All Projects button — always visible */}
      <div className="home-section">
        <div className="section-header">
          <button 
            className="section-link"
            onClick={() => onNavigate && onNavigate('/projects')}
          >
            All Projects <Icon name="arrow-right" size={14} />
          </button>
        </div>
      </div>

      {/* Recently Visited Projects from localStorage */}
      {recentProjectsVisited.length > 0 && (
        <div className="home-section">
          <div className="section-header">
            <h2 className="section-title">Recent Projects</h2>
          </div>
          <div className="projects-grid">
            {recentProjectsVisited.map((project, index) => (
              <ProjectCard
                key={project._id || project.projectId || project.id || `project-${index}`}
                project={project}
                onClick={() => onSelectProject && onSelectProject(project)}
                hideFavorite={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Templates Preview */}
      {showPopularTemplates && templates.length > 0 && (
        <div className="home-section">
          <div className="section-header">
            <h2 className="section-title">Popular Templates</h2>
            <button 
              className="section-link"
              onClick={() => setView('templates')}
            >
              View all <Icon name="arrow-right" size={14} />
            </button>
          </div>
          <div className="templates-grid">
            {templates.slice(0, 3).map((template, idx) => (
              <TemplateCard
                key={idx}
                template={template}
                onSelect={() => onSelectTemplate && onSelectTemplate(template)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="home-footer">
        <p>Made by Mia-Platform with ❤️ :-)</p>
      </div>
    </div>
  );

  const renderProjectsView = () => (
    <div className="home-projects-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('welcome')}>
          <Icon name="arrow-left" size={16} />
          Back
        </button>
        <h1 className="view-title">Projects</h1>
      </div>

      {/* Search and Filters */}
      <div className="view-controls">
        <div className="search-bar">
          <Icon name="search" className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All projects
          </button>
          <button 
            className={`filter-tab ${selectedCategory === 'recent' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('recent')}
          >
            Recent
          </button>
          <button 
            className={`filter-tab ${selectedCategory === 'starred' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('starred')}
          >
            <Icon name="star" size={14} /> Starred
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="projects-grid large">
        {filteredProjects.length === 0 ? (
          <div className="empty-state">
            <Icon name="folder" size={48} className="empty-icon" />
            <h3>No projects found</h3>
            <p>Try adjusting your search or create a new project</p>
            <button className="primary-btn" onClick={() => setView('welcome')}>
              Create Project
            </button>
          </div>
        ) : (
          filteredProjects.map((project, index) => (
            <ProjectCard
              key={project._id || project.projectId || project.id || `project-${index}`}
              project={project}
              onSelect={() => onSelectProject && onSelectProject(project)}
              large
            />
          ))
        )}
      </div>
    </div>
  );

  const renderTemplatesView = () => (
    <div className="home-templates-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('welcome')}>
          <Icon name="arrow-left" size={16} />
          Back
        </button>
        <h1 className="view-title">Templates</h1>
      </div>

      {/* Search */}
      <div className="view-controls">
        <div className="search-bar">
          <Icon name="search" className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Templates Grid */}
      <div className="templates-grid large">
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <Icon name="template" size={48} className="empty-icon" />
            <h3>No templates found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : (
          filteredTemplates.map((template, idx) => (
            <TemplateCard
              key={idx}
              template={template}
              onSelect={() => onSelectTemplate && onSelectTemplate(template)}
              large
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="home-container">
      {view === 'welcome' && renderWelcomeView()}
      {view === 'projects' && renderProjectsView()}
      {view === 'templates' && renderTemplatesView()}
    </div>
  );
};

// Template Card Component
const TemplateCard = ({template, onSelect, large = false}) => {
  return (
    <button 
      className={`template-card ${large ? 'large' : ''}`}
      onClick={onSelect}
    >
      <div className="template-preview">
        {template.preview ? (
          <img src={template.preview} alt={template.name} />
        ) : (
          <div className="template-preview-placeholder">
            <Icon name="template" size={32} />
          </div>
        )}
      </div>
      <div className="template-info">
        <h3 className="template-name">{template.name}</h3>
        <p className="template-description">{template.description}</p>
        {template.tags && (
          <div className="template-tags">
            {template.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="template-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  large: PropTypes.bool,
};

Home.propTypes = {
  onStartChat: PropTypes.func,
  onSelectProject: PropTypes.func,
  onSelectTemplate: PropTypes.func,
  onNavigate: PropTypes.func,
  projects: PropTypes.array,
  templates: PropTypes.array,
  user: PropTypes.object,
  selectedTenant: PropTypes.object,
  hasGitConnection: PropTypes.bool,
  playbooks: PropTypes.array,
  onLaunchIntent: PropTypes.func,
};

export default Home;
export { addRecentProject, activateCodeLayout };
